<template>
    <div class="flex flex-col gap-4">
        <div class="flex flex-col gap-4">
            <!-- ASSETS -->
            <div class="flex flex-col gap-2">
                <label class="font-medium text-indigo-600">Assets</label>

                <div class="flex flex-col gap-2 ml-4">
                    <div v-if="form.assets.should_copy" class="flex flex-col gap-2">
                        <span class="font-medium flex items-center gap-1">
                            Copying
                            <span class="font-medium text-indigo-600"> {{ summary.assets_count }}</span>
                            {{ "asset" | pluralizeOnlyS(summary.assets_count) }}
                        </span>

                        <div v-if="form.assets.copy_from_portfolios" class="flex flex-wrap gap-1">
                            <span class="font-medium">From the following portfolios:</span>

                            <span v-for="(portfolioName, index) in summary.assets_portfolio_names">
                                {{ portfolioName + (index !== summary.assets_portfolio_names.length - 1 ? "," : "") }}
                            </span>
                        </div>

                        <div v-if="rememberingPortfolioNames">
                            Copied assets will be linked to the same portfolios as the original team
                        </div>
                        <div v-else-if="targetPortfolioName" class="flex items-center gap-1">
                            All assets will be linked to the
                            <span class="font-medium text-indigo-600"> {{ targetPortfolioName }} </span> portfolio
                        </div>
                        <div v-else class="flex items-center gap-1">
                            Copied assets will be linked to the <span class="font-medium text-indigo-600">default</span> portfolio of the target team
                        </div>

                        <div class="flex items-center gap-1">
                            All linked asset contacts will also be copied to the target team (if not already copied)
                        </div>
                    </div>

                    <div v-else class="text-red-600">Not copying assets</div>
                </div>
            </div>

            <!-- CONTACTS -->
            <div class="flex flex-col gap-2">
                <label class="font-medium text-indigo-600">Contacts</label>

                <div class="flex flex-col gap-2 ml-4">
                    <div v-if="form.contacts.should_copy" class="flex flex-col gap-2">
                        <span class="font-medium flex items-center gap-1">
                            Copying
                            <span class="font-medium text-indigo-600"> {{ summary.contacts_count }} </span>
                            {{ "contact" | pluralizeOnlyS(summary.contacts_count) }}
                        </span>
                    </div>

                    <div v-else class="text-red-600">Not copying contacts</div>
                </div>
            </div>

            <!-- INSPECTIONS -->
            <div class="flex flex-col gap-2">
                <label class="font-medium text-indigo-600">Inspections</label>

                <div class="flex flex-col gap-2 ml-4">
                    <div v-if="form.inspections.should_copy" class="flex flex-col gap-2">
                        <span class="font-medium flex items-center gap-1">
                            Copying <span class="font-medium" v-if="form.inspections.replicate_copies">an estimate of</span>
                            <span class="font-medium text-indigo-600">
                                {{ (form.inspections.replicate_copies ? (summary.inspections_count * 2) : summary.inspections_count) }} </span>
                            {{ "inspection" | pluralizeOnlyS(summary.inspections_count) }}
                        </span>

                        <div v-if="form.inspections.copy_from_portfolios" class="flex flex-wrap gap-1">
                            <span class="font-medium">From the following portfolios:</span>

                            <span v-for="(portfolioName, index) in summary.inspections_portfolio_names">
                                {{ portfolioName + (index !== summary.inspections_portfolio_names.length - 1 ? "," : "") }}
                            </span>
                        </div>

                        <div v-if="rememberingPortfolioNames">
                            Copied inspection assets will be linked to the same portfolios as the original inspection asset
                        </div>
                        <div v-else-if="targetPortfolioName" class="flex items-center gap-1">
                            All inspection assets will be linked to the
                            <span class="font-medium text-indigo-600"> {{ targetPortfolioName }} </span> portfolio
                        </div>
                        <div v-else>
                            Copied inspection assets will be linked to the <span class="font-medium text-indigo-600">default</span> portfolio of the target team
                        </div>

                        <div v-if="form.inspections.starting_date" class="flex items-center gap-1">
                            Inspections before <span class="font-medium text-indigo-600">{{ form.inspections.starting_date | date }}</span> won't be copied.
                            Previous copies of inspections before the starting date
                            <span class="font-medium text-indigo-600">
                                {{ form.inspections.replicate_copies ? "will" : "won't" }}
                            </span>
                            be copied
                        </div>

                        <div v-if="form.inspections.inspector_email" class="flex items-center gap-1">
                            Inspections will be assigned to <span class="font-medium text-indigo-600">{{
                                summary.inspections_inspector_name
                                + " (" + form.inspections.inspector_email + ")"
                            }}</span>
                        </div>

                        <div v-if="form.inspections.include_archived" class="flex items-center gap-1">
                            <span class="font-medium text-indigo-600">Archived</span> inspections will be included
                        </div>

                        <div v-if="form.inspections.include_deleted" class="flex items-center gap-1">
                            <span class="font-medium text-indigo-600">Deleted</span> inspections will be included
                        </div>

                        <div v-if="summary.inspections_assets_count && summary.inspections_assets_count > 0" class="flex items-center gap-1">
                            All <span class="font-medium text-indigo-600">{{ summary.inspections_assets_count }}</span>
                            inspection assets will also be copied to the target team (if not already copied)
                        </div>

                        <div class="flex items-center gap-1">
                            All inspection contacts and inspection assets' contacts will also be copied to the target team (if not already copied)
                        </div>
                    </div>

                    <div v-else class="text-red-600">Not copying inspections</div>
                </div>
            </div>

            <!-- TODOS -->
            <div class="flex flex-col gap-2">
                <label class="font-medium text-indigo-600">To-dos</label>

                <div class="flex flex-col gap-2 ml-4">
                    <div v-if="form.todos.should_copy" class="flex flex-col gap-2">
                        <span class="font-medium flex items-center gap-1">
                            Copying
                            <span class="font-medium text-indigo-600">
                                {{ summary.todos_count }} </span>
                            {{ "to-do" | pluralizeOnlyS(summary.todos_count) }}
                        </span>

                        <div v-if="form.todos.assign_todos.account_owner ||
                                 (form.todos.assign_todos.user_emails.length === 0 && !form.todos.assign_todos.by_portfolio && !form.todos.assign_todos.account_owner)">
                            To-dos will be assigned to the target team's account owner
                        </div>

                        <div v-if="summary.todos_assignee_usernames">
                            To-dos will be assigned to the following users:
                            <span v-for="(assigneeName, index) in summary.todos_assignee_usernames">
                                {{ assigneeName + (index !== summary.todos_assignee_usernames.length - 1 ? "," : "") }}
                            </span>
                        </div>

                        <div class="flex items-center gap-1">
                            All to-dos' assets will also be copied to the target team (if not already copied)
                        </div>
                    </div>

                    <div v-else class="text-red-600">Not copying to-dos</div>
                </div>
            </div>
        </div>

        <div class="flex justify-end">
            <secondary-button @click="$emit('show-migration-confirmation')">Migrate Team Data</secondary-button>
        </div>
    </div>
</template>

<script>
export default {
    name: "MigrateTeamSummary",

    props: {
        form: {
            type: Object,
            required: true
        },
        summary: {
            type: Object,
            required: true
        },
    },

    computed: {
        rememberingPortfolioNames () {
            return this.form.assets.remember_portfolio_names || this.form.inspections.remember_portfolio_names;
        },

        targetPortfolioName () {
            return this.form.assets.target_portfolio_name || this.form.inspections.target_portfolio_name;
        },
    },
};
</script>